import { FeatureFlagsContext } from '@/common/featureFlagsWrapper';
import { useAccess } from '@/common/hooks/useAccess';
import {
  Box,
  Button,
  CircularProgress,
  InputBase,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAdmin, isManager } from '../../common/actionHelper';
import { NoraPermissionV2 } from '../../common/constants/roles';
import { FetchService } from '../../common/fetch.service';
import { EditSpecializationModal } from './EditSpecializationModal';
import { IUnderwriterWithSpecialization, SpecializationsData } from './types';
import { UnderwriterTable } from './UnderwriterTable';

export function CaseAssignmentPage() {
  const navigate = useNavigate();
  const { flags } = useContext(FeatureFlagsContext);
  const canAccessCaseAssignmentPage = useAccess({
    permission: NoraPermissionV2.VIEW_CONTROL_PANEL,
    legacyCheck: r =>
      isAdmin(r) ||
      (isManager(r) && flags.controlPanelUnderwritingManagerEnabled),
  });

  const [selectedUnderwriter, setSelectedUnderwriter] =
    useState<IUnderwriterWithSpecialization | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const { data: underwriters = [], isLoading: isLoadingUnderwriters } =
    useQuery<IUnderwriterWithSpecialization[]>({
      queryKey: ['underwriters-specializations'],
      queryFn: () =>
        FetchService.getInstance().get(
          '/users/underwriters/with-specializations',
        ),
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });

  const { data: specializations, isLoading: isLoadingSpecializations } =
    useQuery<SpecializationsData>({
      queryKey: ['specialization-options'],
      queryFn: () => FetchService.getInstance().get('/specializations'),
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });

  const filteredUnderwriters = useMemo(() => {
    if (!searchTerm) return underwriters;

    const searchLower = searchTerm.toLowerCase();
    return underwriters.filter(
      uw =>
        uw.displayName?.toLowerCase().includes(searchLower) ||
        uw.firstName?.toLowerCase().includes(searchLower) ||
        uw.lastName?.toLowerCase().includes(searchLower),
    );
  }, [underwriters, searchTerm]);

  if (!canAccessCaseAssignmentPage) {
    return (
      <Box
        sx={{
          paddingInline: 6,
          paddingBlock: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant="h1">
          You are not authorized to access this page
        </Typography>
        <Box sx={{ width: 250 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              navigate('/');
            }}
          >
            Go to Dashboard
          </Button>
        </Box>
      </Box>
    );
  }

  if (isLoadingUnderwriters || isLoadingSpecializations) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          paddingInline: 6,
          paddingBlock: 2,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="h1">Loading...</Typography>
      </Box>
    );
  }

  if (!specializations) {
    return (
      <Box>
        <Typography variant="h1">No specializations found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ paddingInline: 6, paddingBlock: 2 }}>
      <Typography variant="h1" sx={{ fontSize: '1.75rem', fontWeight: 600 }}>
        Case Assignment
      </Typography>
      <Typography variant="body2" sx={{ mb: 4 }}>
        See and edit the Underwriters specializations below
      </Typography>

      <Box
        sx={{
          borderRadius: 2,
          border: '1px solid var(--neutrals-night-20)',
          width: 250,
          mb: 2,
        }}
      >
        <InputBase
          placeholder="Search by underwriter name.."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          sx={{ width: 250, px: 1, fontWeight: 400, fontSize: '0.9rem' }}
        />
      </Box>

      <UnderwriterTable
        underwriters={filteredUnderwriters}
        onEditUnderwriter={setSelectedUnderwriter}
        specializations={specializations}
      />

      {selectedUnderwriter && (
        <EditSpecializationModal
          open={!!selectedUnderwriter}
          onClose={() => setSelectedUnderwriter(null)}
          underwriter={selectedUnderwriter}
          specializations={specializations}
          isLoadingSpecializations={isLoadingSpecializations}
        />
      )}
    </Box>
  );
}
